import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../apiClient';
import { getWebConfig } from '../config';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading, login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [loginRequested, setLoginRequested] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    const dept = searchParams.get('dept');

    if (!dept) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (!isAuthenticated) {
      if (!loginRequested) {
        setLoginRequested(true);
        login();
      }
      return;
    }

    if (!user) {
      return;
    }

    if (!processing) {
      handleDepartmentCallback(dept);
    }
  }, [isAuthenticated, isLoading, login, loginRequested, navigate, processing, searchParams, user]);

  const handleDepartmentCallback = async (departmentSlug: string) => {
    setProcessing(true);

    try {
      const response = await api.auth.createSessionToken({
        entraUserId: user!.entraUserId || '',
        email: user!.email,
        departmentSlug,
      });

      const { sessionToken } = response;

      // Build tenant-web redirect URL
      // Priority: VITE_TENANT_WEB_URL (explicit) > localhost detection > subdomain-based
      const tenantWebOverride = import.meta.env.VITE_TENANT_WEB_URL;
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      let tenantWebUrl: string;
      if (tenantWebOverride) {
        const base = tenantWebOverride.replace(/\/$/, '');
        tenantWebUrl = `${base}/auth/accept?token=${sessionToken}&dept=${departmentSlug}`;
      } else if (isLocalhost) {
        const tenantWebBase = `${window.location.protocol}//${window.location.hostname}:5173`;
        tenantWebUrl = `${tenantWebBase}/auth/accept?token=${sessionToken}&dept=${departmentSlug}`;
      } else {
        const baseDomain = getWebConfig().tenantWebBaseDomain;
        tenantWebUrl = `${window.location.protocol}//${departmentSlug}.${baseDomain}/auth/accept?token=${sessionToken}`;
      }

      window.location.href = tenantWebUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to complete authentication. Please try again.');
      setProcessing(false);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-5 text-center">
        <h2 className="text-xl font-bold text-red-700 mb-4">Authentication Error</h2>
        <p className="mb-6 max-w-sm text-sm text-gray-600">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-red-700 hover:bg-red-800 transition-colors"
        >
          Return to Login
        </button>
      </div>
    );
  }

  return null; // HTML boot splash stays visible
}
